import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Fab,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import moment from "moment-timezone";
import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { LoadingButton } from "@mui/lab";
import ReactSignatureCanvas from "react-signature-canvas";
import ShopS3PresignedCardImage from "../../../components/ShopS3PresignedCardImage";
import {
  CameraAlt,
  Check,
  Close,
  Delete,
  Edit,
  HowToReg,
  KeyboardArrowUp,
  Undo,
  Upload,
} from "@mui/icons-material";
import FileAttachment from "../../../components/FileUpload/FileAttachment";
import ShopS3PresignedImage from "../../../components/ShopS3PresignedImage";
import DownloadIcon from "@mui/icons-material/Download";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import getPickupPackingListDocDefinition from "../../../pdf-helpers/PickupPackingList";
import QRCode from "qrcode";
import OrderTitle from "../../../components/OrderTitle";
import { SnackAlertContext } from "../../../context/SnackAlertContext";
import ApprovePickupWithoutSignatureDialog from "./ApprovePickupWithoutSignatureDialog";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const PickupDetails = () => {
  const { orderId, pickupId } = useParams();
  const { ezorder, authToken, decoded, role } = useContext(AuthContext);
  const { openSnackMessage } = useContext(SnackAlertContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [orderDetails, setOrderDetails] = useState(null);
  const [pickupDetails, setPickupDetails] = useState(null);
  const [pickupTransactions, setPickupTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const signatureRef = useRef();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [receivedBy, setReceivedBy] = useState("");
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [imageUploads, setImageUploads] = useState([]);
  const uploadRef = useRef();
  const cameraRef = useRef();
  const [uploadedFileS3Keys, setUploadedFileS3Keys] = useState([]);
  const [pickupImages, setPickupImages] = useState([]);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [apiError, setApiError] = useState("");
  const [isApproving, setIsApproving] = useState(false);

  const [actionsAnchor, setActionsAnchor] = useState(null);

  const handleCloseActionMenu = () => {
    setActionsAnchor(null);
  };

  const getPickupDetails = async (orderId, pickupId) => {
    try {
      setLoading(true);
      const res = await ezorder.get(
        `/shop/jobs/${orderId}/pickup-history/pickups/${pickupId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );
      setPickupTransactions(res.data.pickup.transactions);
      setPickupDetails(res.data.pickup);
      let imgs = res.data.pickup.images;
      setPickupImages(imgs);
    } catch (error) {
      console.log(error);
      // setError(error);
    } finally {
      setLoading(false);
    }
  };

  const getOrderWithPickupHistory = async (orderId) => {
    try {
      setLoading(true);
      const res = await ezorder.get(`/shop/jobs/${orderId}/pickup-history`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      setOrderDetails(res.data.order);
    } catch (error) {
      console.log(error);
      // setError(error);
    } finally {
      setLoading(false);
    }
  };

  const updatePickupImages = async () => {
    try {
      setLoading(true);
      let images = [];
      if (pickupImages) {
        images.push(...pickupImages);
      }
      if (uploadedFileS3Keys) {
        images.push(...uploadedFileS3Keys);
      }
      const res = await ezorder.put(
        `/shop/jobs/${orderId}/pickup-history/pickups/${pickupId}/images`,
        {
          images,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );
      let imgs = res.data.pickup.images;
      setPickupImages(imgs);
    } catch (error) {
      console.log(error);
      // setError(error);
    } finally {
      setLoading(false);
      setUploadedFileS3Keys([]);
      setImageUploads([]);
    }
  };

  const getBase64FromS3Key = async (s3Key) => {
    try {
      let keySplit = s3Key.split("/");
      let identifier = keySplit[0];
      let resource = keySplit[1];
      let fileName = keySplit[2];

      let requestUrl = "/files";
      let requestData = {
        fileName,
        resource,
        identifier,
      };

      let data = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        params: requestData,
      };

      const response = await ezorder.get(requestUrl, data);
      const contentType = response.headers["content-type"];
      console.log("Base64 Response", response);
      // let base64 =
      //   `data:${contentType};base64,` +
      //   Buffer.from(response.data, "base64").toString("base64");
      let base64 = `data:${contentType};base64,${response.data}`;
      return base64;
      // return response.data;
    } catch (error) {
      throw Error("Unable to load presigned URL", { cause: error });
    }
  };

  const getPackingList = async () => {
    try {
      // TODO: QR CODE Image
      let qrDataURL = await QRCode.toDataURL(
        `${window.location.host}/job-pickups/${orderDetails.id}/pickups/${pickupId}`,
      )
        .then((url) => {
          return url;
        })
        .catch((err) => {
          console.error(err);
        });

      let signatureBase64 = null;
      if (pickupDetails.signature) {
        signatureBase64 = await getBase64FromS3Key(pickupDetails.signature);
      }

      console.log("QR CODE URL", qrDataURL);

      const docDef = getPickupPackingListDocDefinition(
        orderDetails,
        pickupDetails,
        qrDataURL,
        signatureBase64,
      );

      // pdfMake.createPdf(docDef).open();
      pdfMake.createPdf(docDef).download(
        // `PACKING-LIST-${order.id}-${order.company.name}-${order.project.name}.pdf`
        `PACKING-LIST.pdf`,
      );
    } catch (error) {
      console.log("ERROR", error);
      throw Error("Unable to load PDF", { cause: error });
    }
  };

  const deletePickup = async () => {
    try {
      setIsRefreshing(true);
      let response = await ezorder.delete(
        `/shop/jobs/${orderId}/pickup-history/pickups/${pickupId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      if (response.status == 200 || response.status == 201) {
        openSnackMessage(
          "success",
          pickupDetails && pickupDetails.pickupType == "DELIVERY"
            ? "Delivery Deleted"
            : "Pickup Deleted",
        );
        navigate(-1);
      } else {
        // openSnackMessage("error", response.response.data.error);
        openSnackMessage("error", response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        // openSnackMessage("error", error.response.data.error);
        setApiError(error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        // openSnackMessage("error", error);
        setApiError(error);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const completeWithoutSignature = async () => {
    try {
      setIsApproving(true);
      setApiError("");
      const response = await ezorder.put(
        `/shop/jobs/${orderId}/pickup-history/pickups/${pickupId}/complete`,
        {
          // signature: signatureS3Path,
          // receivedBy,
          // disclaimerChecked,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );
      if (response.status == 200 || response.status == 201) {
        getOrderWithPickupHistory(orderId);
        getPickupDetails(orderId, pickupId);
        openSnackMessage(
          "success",
          pickupDetails && pickupDetails.pickupType == "DELIVERY"
            ? "Delivery Completed"
            : "Pickup Completed",
        );
        setApproveDialogOpen(false);
      } else {
        // openSnackMessage("error", response.response.data.error);
        setApiError(response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        // openSnackMessage("error", error.response.data.error);
        setApiError(error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        // openSnackMessage("error", error);
        setApiError(error);
      }
    } finally {
      setIsApproving(false);
    }
  };

  useEffect(() => {
    console.log("uploadedFileS3Keys", uploadedFileS3Keys);
  }, [uploadedFileS3Keys]);

  useEffect(() => {
    if ((orderId, pickupId)) {
      getOrderWithPickupHistory(orderId);
      getPickupDetails(orderId, pickupId);
    }
  }, [orderId, pickupId]);

  if (orderDetails && pickupDetails)
    return (
      <Box sx={{ paddingBottom: "200px" }}>
        <Typography variant="h6">
          <Typography variant="h5" sx={{ textAlign: "center" }}>
            {pickupDetails && pickupDetails.pickupType == "DELIVERY"
              ? "Delivery"
              : "Pickup"}{" "}
            #{pickupDetails ? pickupDetails.id : ""} (
            {pickupDetails && pickupDetails.pickupStatus == "SAVED"
              ? "PENDING"
              : pickupDetails.pickupStatus}
            )
          </Typography>
        </Typography>
        <Box>
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Box sx={{ flex: 1 }}>
              {orderDetails &&
                orderDetails.company &&
                orderDetails.project &&
                orderDetails.orderStatus && (
                  <Box sx={{ marginBottom: "16px" }}>
                    <Typography variant="h7" color={"#c0c0c0"}>
                      Order #{orderDetails.id} - {orderDetails.company.name}
                    </Typography>
                    <Typography
                      variant="h7"
                      sx={{ display: "block" }}
                      color={"#c0c0c0"}
                    >
                      {orderDetails.company.isVendorOrSupplier
                        ? "Vendor/Supplier Customer"
                        : "Project"}
                      : {orderDetails.project.name}
                    </Typography>

                    {orderDetails.vendorProjectName && (
                      <Typography
                        variant="h7"
                        sx={{ display: "block" }}
                        color="#c0c0c0"
                      >
                        Project: {orderDetails.vendorProjectName}
                      </Typography>
                    )}
                    {orderDetails.subProject && (
                      <Typography
                        variant="h7"
                        sx={{ display: "block" }}
                        color="#c0c0c0"
                      >
                        Sub-Project: {orderDetails.subProject}
                      </Typography>
                    )}

                    <Typography
                      variant="h7"
                      sx={{ display: "block" }}
                      color={"#c0c0c0"}
                    >
                      {orderDetails.poNumber
                        ? `PO#: ${orderDetails.poNumber}`
                        : ""}
                    </Typography>

                    <Button
                      sx={{ padding: 0, margin: 0 }}
                      onClick={() => {
                        navigate(`/orders/${orderDetails.id}`);
                      }}
                    >
                      View Order
                    </Button>
                  </Box>
                )}
            </Box>
          </Box>
        </Box>
        {loading || !orderDetails ? (
          <Box
            sx={{
              width: "100%",
              minHeight: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size="3rem" />
          </Box>
        ) : (
          <Box sx={{ marginTop: "16px" }}>
            <Box
              sx={
                isMediumScreen
                  ? {
                      marginTop: "32px",
                      display: "flex",
                      flexDirection: "column",
                    }
                  : {
                      marginTop: "32px",
                      display: "flex",
                      flexDirection: "row",
                    }
              }
            >
              <Box
                sx={{
                  margin: 0,
                  padding: 0,
                  minWidth: isMediumScreen ? "100%" : "50%",
                  flex: 1,
                }}
              >
                <TableContainer component={Card}>
                  <Box sx={{ display: "flex", flexDirection: "row" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        flex: 1,
                        padding: "16px",
                      }}
                    >
                      {pickupDetails && pickupDetails.pickupType == "DELIVERY"
                        ? "Delivery"
                        : "Pickup"}{" "}
                      Summary
                    </Typography>

                    <Divider />
                  </Box>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell align="left">Description</TableCell>
                        <TableCell align="left" sx={{ width: "70px" }}>
                          Type
                        </TableCell>
                        <TableCell align="left" sx={{ width: "70px" }}>
                          Quantity
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pickupTransactions &&
                        pickupTransactions.map((transaction, index) => {
                          return (
                            <>
                              <TableRow key={transaction.id}>
                                <TableCell align="left">
                                  {transaction.pickupItem.itemName}
                                  {/* <Typography variant="caption">
                                  {transaction.pickupItem.objectType}
                                </Typography> */}
                                </TableCell>
                                <TableCell align="left">
                                  {transaction.pickupItem.objectType}
                                </TableCell>
                                <TableCell align="left">
                                  <Typography variant="caption">
                                    {transaction.quantity}{" "}
                                    {transaction.pickupItem.uom}
                                  </Typography>
                                </TableCell>

                                <Divider />
                              </TableRow>
                            </>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Box
                sx={
                  isMediumScreen
                    ? {
                        marginTop: "32px",
                        width: isMediumScreen ? "100%" : "50%",
                      }
                    : { marginLeft: "32px" }
                }
              >
                {pickupDetails.signature && pickupDetails.signedOn && (
                  <Card
                    sx={{
                      padding: "16px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant="h6">
                      Review & Sign Packing List (Signed On{" "}
                      {moment(pickupDetails.signedOn)
                        .tz("America/Phoenix")
                        .format("MM/DD/YY")}
                      )
                    </Typography>
                    <IconButton
                      sx={{ marginTop: "8px" }}
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        getPackingList();
                      }}
                    >
                      Packing List
                      <DownloadIcon />
                    </IconButton>
                    <Typography sx={{ width: "100%" }}>Disclaimer</Typography>
                    <Typography sx={{ width: "100%" }}>
                      We hereby accept the quantities listed herein, and release
                      Industry Metal Co of any discrepancies in regards to
                      quantities
                    </Typography>

                    <Box sx={{ marginTop: "16px" }}>
                      <Typography>
                        <Checkbox
                          style={{
                            margin: 0,
                            padding: 0,
                          }}
                          disabled
                          checked={pickupDetails.disclaimerChecked}
                        />
                        I HEREBY ACCEPT THE QUANTITY AND CONDITION OF THE
                        MATERIALS PACKAGED HEREIN
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        marginBottom: "16px",
                      }}
                    >
                      <Box>
                        <TextField
                          disabled
                          fullWidth
                          sx={{ marginTop: "16px", marginBottom: "16px" }}
                          label="Print Name"
                          value={pickupDetails.receivedBy}
                          InputProps={{ readOnly: true }}
                        />

                        <Card
                          sx={{
                            background: "#d2d2d2",
                            // width: 310,
                            // height: 100,
                          }}
                        >
                          <ShopS3PresignedImage
                            style={
                              {
                                // width: "310px",
                                // height: "100px",
                              }
                            }
                            s3Key={pickupDetails.signature}
                          />
                        </Card>
                      </Box>
                    </Box>
                  </Card>
                )}
              </Box>
            </Box>
          </Box>
        )}
        {(!pickupDetails.pickupStatus != "COMPLETED" ||
          !pickupDetails.signature) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconButton
              sx={{ marginTop: "8px" }}
              variant="outlined"
              color="primary"
              onClick={() => {
                getPackingList();
              }}
            >
              Packing List
              <DownloadIcon />
            </IconButton>
          </Box>
        )}
        <Card sx={{ marginTop: "16px", padding: "16px" }}>
          <Typography variant="h6">Add Images</Typography>
          <Grid
            container
            padding={0}
            spacing={0}
            alignItems="center"
            justifyContent="center"
          >
            {
              //Saved Images
              pickupImages &&
                pickupImages.map((s3Key, index) => {
                  console.log("hello", s3Key);
                  return (
                    <Grid
                      item
                      key={index}
                      style={{
                        position: "relative",
                        margin: 0,
                        padding: 0,
                      }}
                    >
                      <ShopS3PresignedCardImage
                        key={index}
                        width={100}
                        s3Key={s3Key}
                        style={{
                          cursor: "pointer",
                        }}
                      />
                      <Close
                        color="error"
                        style={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          let pickupImagesCopy = [...pickupImages];
                          pickupImagesCopy.splice(index, 1);
                          setPickupImages(pickupImagesCopy);
                        }}
                      />
                    </Grid>
                  );
                })
            }
          </Grid>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <label>
              <input
                ref={uploadRef}
                style={{ display: "none" }}
                type="file"
                multiple
                accept="image/*"
                // capture="environment"
                onChange={(event) => {
                  let filesAndLocalUrls = [];
                  for (let fileToUpload of event.target.files) {
                    const localUrl = URL.createObjectURL(fileToUpload);
                    filesAndLocalUrls.push({
                      file: fileToUpload,
                      localUrl,
                    });
                  }
                  console.log(filesAndLocalUrls);
                  setImageUploads((prev) => [...prev, ...filesAndLocalUrls]);
                }}
              />
              <div
                style={{
                  cursor: "pointer",
                  width: "100%",
                  padding: 12,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    borderStyle: "solid",
                    borderWidth: 1,
                    borderColor: "#d2d2d2",
                    color: "#d2d2d2",
                    width: 100,
                    height: 100,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Upload sx={{ fontSize: 40 }} />
                </div>
              </div>
            </label>
            <label>
              <input
                multiple
                ref={cameraRef}
                style={{ display: "none" }}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(event) => {
                  let filesAndLocalUrls = [];
                  for (let fileToUpload of event.target.files) {
                    const localUrl = URL.createObjectURL(fileToUpload);
                    filesAndLocalUrls.push({
                      file: fileToUpload,
                      localUrl,
                    });
                  }
                  console.log(filesAndLocalUrls);
                  setImageUploads((prev) => [...prev, ...filesAndLocalUrls]);
                }}
              />
              <div
                style={{
                  cursor: "pointer",
                  width: "100%",
                  padding: 12,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    borderStyle: "solid",
                    borderWidth: 1,
                    borderColor: "#d2d2d2",
                    color: "#d2d2d2",
                    width: 100,
                    height: 100,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CameraAlt sx={{ fontSize: 40 }} />
                </div>
              </div>
            </label>
          </div>
          <Grid
            container
            padding={0}
            spacing={0}
            alignItems="center"
            justifyContent="center"
          >
            {
              //Unsaved Images
              imageUploads.map((imageUpload, index) => (
                <Grid
                  item
                  key={index}
                  style={{
                    position: "relative",
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <FileAttachment
                    s3Key={imageUpload.s3Key}
                    identifier={orderDetails.company.id}
                    file={imageUpload.file}
                    localFileUrl={imageUpload.localUrl}
                    onDelete={(s3Key) => {
                      // TODO: onDelete
                      // delete s3Key from list
                      // remove imageUpload item
                      let imageUploadsCopy = [...imageUploads];
                      imageUploadsCopy.splice(index, 1);
                      setImageUploads(imageUploadsCopy);
                      let uploadedFileS3KeysCopy = [...uploadedFileS3Keys];
                      let idxToRemove = uploadedFileS3KeysCopy.findIndex(
                        (key) => key == s3Key,
                      );
                      if (idxToRemove) {
                        uploadedFileS3KeysCopy.splice(idxToRemove, 1);
                        setUploadedFileS3Keys(uploadedFileS3KeysCopy);
                      }
                    }}
                    onUploaded={(s3Key) => {
                      // TODO onUploaded
                      // console.log("UPLOADED", s3Key);
                      setUploadedFileS3Keys((prev) => [...prev, s3Key]);
                      let imageUploadsCopy = [...imageUploads];
                      imageUploadsCopy[index].s3Key = s3Key;
                      setImageUploads(imageUploadsCopy);

                      // Add uploaded images
                      // setPickupImages((prev) => [...prev, s3Key]);

                      // Delete from being uploaded
                      // let imageUploadsCopy = [...imageUploads];
                      // imageUploadsCopy.splice(index, 1);
                      // setImageUploads(imageUploadsCopy);
                      // let uploadedFileS3KeysCopy = [
                      //   ...uploadedFileS3Keys,
                      // ];
                      // let idxToRemove = uploadedFileS3KeysCopy.findIndex(
                      //   (key) => key == s3Key
                      // );
                      // if (idxToRemove) {
                      //   uploadedFileS3KeysCopy.splice(idxToRemove, 1);
                      //   setUploadedFileS3Keys(uploadedFileS3KeysCopy);
                      // }
                    }}
                  />
                </Grid>
              ))
            }
          </Grid>
          <Box
            disabled={imageUploads.length != uploadedFileS3Keys.length}
            onClick={() => {
              updatePickupImages();
            }}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button>Update Images</Button>
          </Box>
        </Card>
        <ApprovePickupWithoutSignatureDialog
          open={approveDialogOpen}
          handleClose={() => {
            setApproveDialogOpen(false);
          }}
          onConfirm={() => {
            completeWithoutSignature();
          }}
          loading={isApproving}
          errorMessage={apiError}
        />
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            right: isSmallScreen ? "16px" : "50px",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <>
            <Fab
              onClick={(event) => {
                setActionsAnchor(event.currentTarget);
              }}
              variant="extended"
              color="success"
            >
              Actions <KeyboardArrowUp />
            </Fab>
            <Menu
              id="demo-customized-menu"
              slotProps={{
                list: {
                  "aria-labelledby": "demo-customized-button",
                },
              }}
              anchorEl={actionsAnchor}
              open={actionsAnchor}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              onClose={handleCloseActionMenu}
            >
              <MenuItem
                disableRipple
                disabled={
                  !pickupDetails || pickupDetails.pickupStatus == "COMPLETED"
                }
                onClick={() => {
                  handleCloseActionMenu();
                  navigate(`/job-pickups/${orderId}/pickups/${pickupId}/edit`);
                }}
              >
                <Edit sx={{ marginRight: "8px" }} />
                Edit Items
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              {decoded.role === "EZORDER_ADMIN" && (
                <>
                  <MenuItem
                    disableRipple
                    disabled={
                      pickupDetails.pickupStatus != "SAVED" ||
                      decoded.role !== "EZORDER_ADMIN"
                    }
                    onClick={() => {
                      handleCloseActionMenu();
                      deletePickup();
                    }}
                  >
                    <Delete sx={{ marginRight: "8px" }} />
                    Delete
                  </MenuItem>
                  <Divider sx={{ my: 0.5 }} />
                </>
              )}

              <MenuItem
                disableRipple
                disabled={
                  !pickupDetails || pickupDetails.pickupStatus == "COMPLETED"
                }
                onClick={() => {
                  handleCloseActionMenu();
                  navigate(
                    `/job-pickups/${orderId}/pickups/${pickupId}/review`,
                  );
                }}
              >
                <HowToReg sx={{ marginRight: "8px" }} />
                Customer Review & Sign
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                // disableRipple
                disabled={
                  !pickupDetails || pickupDetails.pickupStatus == "COMPLETED"
                }
                onClick={() => {
                  handleCloseActionMenu();
                  setApproveDialogOpen(true);
                }}
              >
                <Check sx={{ marginRight: "8px" }} />
                Complete w/o Customer Signature
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
            </Menu>
          </>
        </div>
      </Box>
    );
};

export default PickupDetails;
